import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { usePageHeader } from '../../components/Layout';
import { Ic } from '../../design/icons';
import { Avatar, Chip } from '../../design/primitives';
import { useWorkspace } from '../../contexts/WorkspaceContext';

const API = `${window.location.origin}/api`;

const PLAN_LABELS = {
  free: 'Free',
  starter: 'Starter',
  growth: 'Growth',
  scale: 'Scale',
  unlimited: 'Sınırsız',
};

const STATUS_LABELS = {
  active: 'Aktif',
  past_due: 'Ödeme Bekliyor',
  cancelled: 'İptal',
  grace: 'Süre Tanındı',
};

function formatTry(kurus) {
  if (kurus == null) return '—';
  const tl = (kurus / 100).toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  return `₺${tl}`;
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function statusChip(status) {
  if (status === 'past_due')
    return <Chip color="var(--warning)" bg="var(--warning-weak)">{STATUS_LABELS.past_due}</Chip>;
  if (status === 'cancelled')
    return <Chip color="var(--danger)" bg="var(--surface-muted)">{STATUS_LABELS.cancelled}</Chip>;
  if (status === 'grace')
    return <Chip color="var(--accent)" bg="var(--surface-muted)">{STATUS_LABELS.grace}</Chip>;
  return <Chip color="var(--good)" bg="var(--good-weak)">{STATUS_LABELS.active}</Chip>;
}

export default function GlobalWorkspaces() {
  usePageHeader([{ label: 'Süper Admin' }, { label: "Tüm Workspace'ler" }], null, []);

  const navigate = useNavigate();
  const { switchWorkspace, refresh: refreshWorkspaces } = useWorkspace();

  const [workspaces, setWorkspaces] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [filterPlan, setFilterPlan] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const [editTarget, setEditTarget] = useState(null);
  const [editDraft, setEditDraft] = useState({ name: '', plan: '', status: '', seatLimit: '', projectLimit: '' });
  const [saving, setSaving] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [createDraft, setCreateDraft] = useState({ name: '', plan: 'free', setAsDefault: false });
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [ownerSearch, setOwnerSearch] = useState('');
  const [ownerOptions, setOwnerOptions] = useState([]);
  const [ownerLoading, setOwnerLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debounced, filterPlan, filterStatus]);

  const fetchWorkspaces = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '50' });
      if (debounced) params.append('q', debounced);
      if (filterPlan !== 'all') params.append('plan', filterPlan);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      const res = await axios.get(`${API}/super-admin/workspaces?${params.toString()}`);
      setWorkspaces(res.data.workspaces || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.totalPages || 1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Workspace'ler yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [page, debounced, filterPlan, filterStatus]);

  useEffect(() => {
    fetchWorkspaces();
  }, [fetchWorkspaces]);

  useEffect(() => {
    if (!createOpen) return;
    let active = true;
    setOwnerLoading(true);
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ limit: '20' });
        if (ownerSearch.trim()) params.append('q', ownerSearch.trim());
        const res = await axios.get(`${API}/super-admin/users?${params.toString()}`);
        if (active) setOwnerOptions(res.data.users || []);
      } catch {
        if (active) setOwnerOptions([]);
      } finally {
        if (active) setOwnerLoading(false);
      }
    }, 280);
    return () => { active = false; clearTimeout(t); };
  }, [createOpen, ownerSearch]);

  const openCreate = () => {
    setCreateDraft({ name: '', plan: 'free', setAsDefault: false });
    setSelectedOwner(null);
    setOwnerSearch('');
    setOwnerOptions([]);
    setCreateOpen(true);
  };

  const closeCreate = () => {
    if (creating) return;
    setCreateOpen(false);
  };

  const submitCreate = async () => {
    const name = createDraft.name.trim();
    if (!name) {
      toast.error('Workspace adı boş olamaz');
      return;
    }
    if (!selectedOwner) {
      toast.error('Bir sahip kullanıcı seçin');
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/super-admin/workspaces`, {
        name,
        ownerId: selectedOwner.id,
        plan: createDraft.plan,
        setAsDefault: createDraft.setAsDefault,
      });
      toast.success(`${res.data.workspace?.name || name} oluşturuldu`);
      setCreateOpen(false);
      fetchWorkspaces();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Workspace oluşturulamadı');
    } finally {
      setCreating(false);
    }
  };

  const enterWorkspace = async (w) => {
    try {
      switchWorkspace(w.id);
      // Make sure the sidebar / banner pick up the new active id before navigating
      await refreshWorkspaces?.();
      toast.success(`${w.name} workspace'ine girildi`);
      navigate('/dashboard');
    } catch {
      toast.error('Workspace değiştirilemedi');
    }
  };

  const openEdit = (w) => {
    setEditTarget(w);
    setEditDraft({
      name: w.name || '',
      plan: w.plan || 'free',
      status: w.status || 'active',
      seatLimit: String(w.seatLimit ?? ''),
      projectLimit: String(w.projectLimit ?? ''),
    });
  };

  const saveEdit = async () => {
    if (!editTarget) return;
    setSaving(true);
    try {
      const payload = {};
      if (editDraft.name && editDraft.name !== editTarget.name) payload.name = editDraft.name;
      if (editDraft.plan !== editTarget.plan) payload.plan = editDraft.plan;
      if (editDraft.status !== editTarget.status) payload.status = editDraft.status;
      if (editDraft.seatLimit !== '' && Number(editDraft.seatLimit) !== editTarget.seatLimit) {
        payload.seatLimit = Number(editDraft.seatLimit);
      }
      if (editDraft.projectLimit !== '' && Number(editDraft.projectLimit) !== editTarget.projectLimit) {
        payload.projectLimit = Number(editDraft.projectLimit);
      }
      if (Object.keys(payload).length === 0) {
        setEditTarget(null);
        return;
      }
      await axios.patch(`${API}/super-admin/workspaces/${editTarget.id}`, payload);
      toast.success('Workspace güncellendi');
      setEditTarget(null);
      fetchWorkspaces();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Güncelleme başarısız');
    } finally {
      setSaving(false);
    }
  };

  const filtersActive = debounced || filterPlan !== 'all' || filterStatus !== 'all';

  return (
    <div className="ox-page space-y-4" style={{ padding: 24 }}>
      <div className="ox-grid-stats-6">
        <StatTile label="Toplam (filtreli)" value={total} />
        <StatTile label="Aktif" value={workspaces.filter(w => w.status === 'active').length} color="var(--good)" />
        <StatTile label="Ödeme Bekliyor" value={workspaces.filter(w => w.status === 'past_due').length} color="var(--warning)" />
        <StatTile label="İptal" value={workspaces.filter(w => w.status === 'cancelled').length} color="var(--danger)" />
        <StatTile label="Aboneli" value={workspaces.filter(w => w.subscription?.status === 'active').length} color="var(--accent)" />
        <StatTile label="MRR (sayfa)" value={formatTry(workspaces.reduce((acc, w) => acc + (w.subscription?.status === 'active' ? (w.subscription.amount || 0) : 0), 0))} small />
      </div>

      <div className="ox-surface rounded-md p-3 flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-0 sm:min-w-[260px] sm:flex-none basis-full sm:basis-auto">
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 ox-text-dim">{Ic.search}</span>
          <input
            className="ox-input pl-8"
            placeholder="Workspace adı..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select className="ox-select" value={filterPlan} onChange={(e) => setFilterPlan(e.target.value)} style={{ maxWidth: 150 }}>
          <option value="all">Tüm Planlar</option>
          {Object.entries(PLAN_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select className="ox-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={{ maxWidth: 170 }}>
          <option value="all">Tüm Durumlar</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {filtersActive && (
          <button
            className="ox-btn ox-btn-ghost"
            onClick={() => { setSearch(''); setFilterPlan('all'); setFilterStatus('all'); }}
          >
            {Ic.x} Temizle
          </button>
        )}
        <button className="ox-btn ml-auto" onClick={openCreate}>
          {Ic.plus || '+'} Yeni Workspace
        </button>
        <button className="ox-btn ox-btn-ghost" onClick={fetchWorkspaces} title="Yenile">
          {Ic.refresh}
        </button>
      </div>

      <div className="ox-surface rounded-md overflow-hidden">
        {loading ? (
          <div className="p-12 text-center ox-text-muted">Yükleniyor...</div>
        ) : workspaces.length === 0 ? (
          <div className="p-12 text-center ox-text-muted">Workspace bulunamadı</div>
        ) : (
          <div className="ox-table-wrap ox-table-wide">
            <table className="w-full text-[13px]">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--surface-muted)' }}>
                  <th className="text-left px-3 py-2.5 text-[11px] ox-text-muted uppercase tracking-wider font-medium">Workspace</th>
                  <th className="text-left px-3 py-2.5 text-[11px] ox-text-muted uppercase tracking-wider font-medium">Sahip</th>
                  <th className="text-left px-3 py-2.5 text-[11px] ox-text-muted uppercase tracking-wider font-medium">Plan</th>
                  <th className="text-left px-3 py-2.5 text-[11px] ox-text-muted uppercase tracking-wider font-medium">Durum</th>
                  <th className="text-left px-3 py-2.5 text-[11px] ox-text-muted uppercase tracking-wider font-medium hidden md:table-cell">Üye</th>
                  <th className="text-left px-3 py-2.5 text-[11px] ox-text-muted uppercase tracking-wider font-medium hidden lg:table-cell">Abonelik</th>
                  <th className="text-left px-3 py-2.5 text-[11px] ox-text-muted uppercase tracking-wider font-medium hidden lg:table-cell">Sonraki Yenileme</th>
                  <th className="text-right px-3 py-2.5 text-[11px] ox-text-muted uppercase tracking-wider font-medium">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {workspaces.map((w) => (
                  <tr key={w.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td className="px-3 py-2.5">
                      <div className="text-[13px] font-medium truncate">{w.name}</div>
                      <div className="text-[11px] ox-text-muted truncate">{formatDate(w.createdAt)}</div>
                    </td>
                    <td className="px-3 py-2.5">
                      {w.owner ? (
                        <div className="flex items-center gap-2 min-w-0">
                          <Avatar u={w.owner} imgSrc={w.owner.picture} size={24} />
                          <div className="min-w-0">
                            <div className="text-[12.5px] truncate">{w.owner.name || '—'}</div>
                            <div className="text-[11px] ox-text-muted truncate">{w.owner.email}</div>
                          </div>
                        </div>
                      ) : (
                        <span className="ox-text-dim text-[12px]">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <Chip color="var(--accent)" bg="var(--surface-muted)">{PLAN_LABELS[w.plan] || w.plan}</Chip>
                    </td>
                    <td className="px-3 py-2.5">{statusChip(w.status)}</td>
                    <td className="px-3 py-2.5 hidden md:table-cell">
                      <span className="ox-tnum text-[12.5px]">{w.memberCount}</span>
                      <span className="ox-text-dim text-[11px]"> / {w.seatLimit || '—'}</span>
                    </td>
                    <td className="px-3 py-2.5 hidden lg:table-cell">
                      {w.subscription ? (
                        <div>
                          <div className="text-[12.5px]">{formatTry(w.subscription.amount)} <span className="ox-text-muted">/{w.subscription.billingCycle === 'annual' ? 'yıl' : 'ay'}</span></div>
                          <div className="text-[11px] ox-text-muted">{w.subscription.status}</div>
                        </div>
                      ) : (
                        <span className="ox-text-dim text-[12px]">Yok</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 hidden lg:table-cell text-[12px]">
                      {formatDate(w.subscription?.currentPeriodEnd || w.currentPeriodEnd)}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-0.5 justify-end">
                        <button
                          className="ox-btn ox-btn-ghost p-1.5"
                          title="Bu workspace'e gir"
                          onClick={() => enterWorkspace(w)}
                        >
                          {Ic.arrowRight || '→'}
                        </button>
                        <button className="ox-btn ox-btn-ghost p-1.5" title="Düzenle" onClick={() => openEdit(w)}>{Ic.edit}</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-3 py-2.5" style={{ borderTop: '1px solid var(--border)' }}>
            <span className="text-[12px] ox-text-muted">
              {(page - 1) * 50 + 1}–{Math.min(page * 50, total)} / {total}
            </span>
            <div className="flex items-center gap-1">
              <button className="ox-btn ox-btn-ghost" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Önceki</button>
              <button className="ox-btn ox-btn-ghost" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Sonraki</button>
            </div>
          </div>
        )}
      </div>

      {editTarget && (
        <div className="ox-modal-backdrop" onClick={() => !saving && setEditTarget(null)}>
          <div className="ox-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ox-modal-head">
              <div className="font-semibold text-[14px]">Workspace'i Düzenle</div>
              <button className="ox-btn ox-btn-ghost p-1" onClick={() => setEditTarget(null)}>{Ic.x}</button>
            </div>
            <div className="ox-modal-body space-y-3">
              <div className="text-[12px] ox-text-muted">{editTarget.id}</div>
              <div className="ox-field">
                <label>İsim</label>
                <input
                  className="ox-input"
                  value={editDraft.name}
                  onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })}
                />
              </div>
              <div className="grid gap-3" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <div className="ox-field">
                  <label>Plan</label>
                  <select
                    className="ox-select"
                    value={editDraft.plan}
                    onChange={(e) => setEditDraft({ ...editDraft, plan: e.target.value })}
                  >
                    {Object.entries(PLAN_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div className="ox-field">
                  <label>Durum</label>
                  <select
                    className="ox-select"
                    value={editDraft.status}
                    onChange={(e) => setEditDraft({ ...editDraft, status: e.target.value })}
                  >
                    {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid gap-3" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <div className="ox-field">
                  <label>Koltuk Limiti</label>
                  <input
                    type="number" min={0}
                    className="ox-input"
                    value={editDraft.seatLimit}
                    onChange={(e) => setEditDraft({ ...editDraft, seatLimit: e.target.value })}
                  />
                </div>
                <div className="ox-field">
                  <label>Proje Limiti</label>
                  <input
                    type="number" min={0}
                    className="ox-input"
                    value={editDraft.projectLimit}
                    onChange={(e) => setEditDraft({ ...editDraft, projectLimit: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <div className="ox-modal-foot">
              <button className="ox-btn ox-btn-ghost" disabled={saving} onClick={() => setEditTarget(null)}>İptal</button>
              <button className="ox-btn" disabled={saving} onClick={saveEdit}>
                {saving ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {createOpen && (
        <div className="ox-modal-backdrop" onClick={closeCreate}>
          <div className="ox-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="ox-modal-head">
              <div className="font-semibold text-[14px]">Yeni Workspace Oluştur</div>
              <button className="ox-btn ox-btn-ghost p-1" onClick={closeCreate}>{Ic.x}</button>
            </div>
            <div className="ox-modal-body space-y-3">
              <div className="text-[12px] ox-text-muted">
                Süper admin olarak ödeme almadan workspace oluşturup seçtiğiniz kullanıcıya
                sahip (owner) olarak atayabilirsiniz.
              </div>
              <div className="ox-field">
                <label>Workspace Adı</label>
                <input
                  className="ox-input"
                  placeholder="Örn. Acme Pazarlama"
                  value={createDraft.name}
                  onChange={(e) => setCreateDraft({ ...createDraft, name: e.target.value })}
                  autoFocus
                />
              </div>

              <div className="ox-field">
                <label>Plan</label>
                <select
                  className="ox-select"
                  value={createDraft.plan}
                  onChange={(e) => setCreateDraft({ ...createDraft, plan: e.target.value })}
                >
                  {Object.entries(PLAN_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <div className="text-[11.5px] ox-text-muted mt-1">
                  Plan limitleri (koltuk / proje) otomatik uygulanır. Sonradan düzenleyebilirsiniz.
                </div>
              </div>

              <div className="ox-field">
                <label>Sahip (Owner) Kullanıcı</label>
                {selectedOwner ? (
                  <div
                    className="ox-surface rounded-md p-2 flex items-center gap-2"
                    style={{ border: '1px solid var(--border)' }}
                  >
                    <Avatar u={selectedOwner} imgSrc={selectedOwner.picture} size={28} />
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-medium truncate">{selectedOwner.name || '—'}</div>
                      <div className="text-[11px] ox-text-muted truncate">{selectedOwner.email}</div>
                    </div>
                    <button className="ox-btn ox-btn-ghost p-1" onClick={() => setSelectedOwner(null)} title="Değiştir">
                      {Ic.x}
                    </button>
                  </div>
                ) : (
                  <>
                    <input
                      className="ox-input"
                      placeholder="İsim veya e-posta ile ara..."
                      value={ownerSearch}
                      onChange={(e) => setOwnerSearch(e.target.value)}
                    />
                    <div
                      className="ox-surface rounded-md mt-2"
                      style={{
                        border: '1px solid var(--border)',
                        maxHeight: 220,
                        overflow: 'auto',
                      }}
                    >
                      {ownerLoading ? (
                        <div className="p-3 text-center text-[12px] ox-text-muted">Aranıyor...</div>
                      ) : ownerOptions.length === 0 ? (
                        <div className="p-3 text-center text-[12px] ox-text-muted">Kullanıcı bulunamadı</div>
                      ) : (
                        ownerOptions.map((u) => (
                          <button
                            key={u.id}
                            type="button"
                            className="w-full text-left px-2.5 py-2 flex items-center gap-2 hover:bg-[var(--surface-muted)]"
                            style={{ borderBottom: '1px solid var(--border)' }}
                            onClick={() => setSelectedOwner(u)}
                          >
                            <Avatar u={u} imgSrc={u.picture} size={24} />
                            <div className="min-w-0 flex-1">
                              <div className="text-[12.5px] truncate">{u.name || '—'}</div>
                              <div className="text-[11px] ox-text-muted truncate">{u.email}</div>
                            </div>
                            {u.role === 'super_admin' && (
                              <Chip color="#b45309" bg="var(--surface-muted)">Süper Admin</Chip>
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  </>
                )}
              </div>

              <label className="flex items-center gap-2 text-[12.5px] ox-text-muted">
                <input
                  type="checkbox"
                  checked={createDraft.setAsDefault}
                  onChange={(e) => setCreateDraft({ ...createDraft, setAsDefault: e.target.checked })}
                />
                Bu kullanıcının varsayılan workspace'i olarak ayarla
              </label>
            </div>
            <div className="ox-modal-foot">
              <button className="ox-btn ox-btn-ghost" disabled={creating} onClick={closeCreate}>İptal</button>
              <button
                className="ox-btn"
                disabled={creating || !createDraft.name.trim() || !selectedOwner}
                onClick={submitCreate}
              >
                {creating ? 'Oluşturuluyor...' : 'Oluştur ve Ata'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatTile({ label, value, color, small }) {
  return (
    <div className="ox-surface rounded-md p-3">
      <div className="text-[10.5px] ox-text-muted uppercase tracking-wider">{label}</div>
      <div
        className="ox-tnum font-semibold mt-1"
        style={{ color: color || 'var(--text)', fontSize: small ? 16 : 22 }}
      >
        {value}
      </div>
    </div>
  );
}
